import { S3, HeadObjectCommand, paginateListObjectsV2, _Object } from "@aws-sdk/client-s3";

let singletonS3Client: S3 | null = null;

// The surveys bucket has a Pulumi-generated name that differs per stack, so infra
// injects it as an env var (see infra/index.ts lambdaVariables). The fallback keeps
// prod and local dev unchanged. This is the single source of truth across packages.
export const surveysBucketName = process.env.SURVEYS_BUCKET || 'aidkit-surveys-c285c37';

/**
 * Every object under `prefix`, draining the SDK's ListObjectsV2 paginator.
 *
 * A bare listObjectsV2 call silently returns at most 1000 keys in UTF-8 order, so
 * any caller that lists a prefix which can grow past 1000 gets a truncated answer
 * with no error - it just sees the lexicographically-first page. Use this instead
 * whenever the prefix has no hard upper bound.
 *
 * Note this buffers every key in memory, so it is not appropriate for prefixes
 * holding hundreds of thousands of objects.
 */
export async function listAllObjects(params: {
  bucket: string,
  prefix: string,
  client?: S3,
}): Promise<_Object[]> {
  const objects: _Object[] = [];

  for await (const page of paginateListObjectsV2(
    { client: params.client ?? getS3() },
    { Bucket: params.bucket, Prefix: params.prefix },
  )) {
    if (page.Contents) objects.push(...page.Contents);
  }

  return objects;
}

/**
 * Interface for S3 file data with metadata
 */
export interface S3FileData {
  key: string;
  filename: string;
  size: number;
  lastModified: Date;
  metadata: Record<string, string>;
}

/**
 * Fetch S3 object data including its metadata from a ListObjectsV2 result
 * @param obj - S3 _Object from ListObjectsV2Command
 * @param bucket - S3 bucket name
 * @param client - S3 client (optional, defaults to singleton)
 * @returns S3FileData object
 */
export async function fetchS3FileMetadata(
  obj: _Object,
  bucket: string,
  client?: S3
): Promise<S3FileData> {
  const s3Client = client || getS3();
  const key = obj.Key || '';
  const filename = key.split('/').pop() || key;

  const baseFile: S3FileData = {
    key,
    filename,
    size: obj.Size || 0,
    lastModified: obj.LastModified || new Date(),
    metadata: {},
  };

  try {
    const headCommand = new HeadObjectCommand({
      Bucket: bucket,
      Key: key,
    });
    const headResponse = await s3Client.send(headCommand);

    return {
      ...baseFile,
      metadata: headResponse.Metadata || {},
    };
  } catch (error) {
    console.error(`Error fetching metadata for ${key}:`, error);
    return baseFile;
  }
}

export function getS3(): S3 {
  if (!singletonS3Client) {
    const s3 = new S3({ 
      ...((process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') ? {
        endpoint: process.env.AK_BASE ? process.env.AK_BASE + ':4324' : 'http://127.0.0.1:4323',
        sslEnabled: false,
        forcePathStyle: true,
        credentials: {
          accessKeyId: 'test',
          secretAccessKey: 'testtesttest'
        }
      } : {})
    });
    s3.destroy = () => {console.log('Prevented singleton S3Client from being destroyed');};
    singletonS3Client = s3;
  }
  if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
    // Setup the necessary buckets lazily
    createLocalBuckets(singletonS3Client).catch(() => {});
  }
    
  return singletonS3Client;
}

export function getCustomS3(endpoint?: string, localUseAWS?: boolean): S3 {
  const s3 = new S3({ 
    ...((!localUseAWS && (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test')) ? {
      endpoint: endpoint || (process.env.AK_BASE ? process.env.AK_BASE + ':4324' : 'http://127.0.0.1:4323'),
      sslEnabled: false,
      forcePathStyle: true,
      credentials: {
        accessKeyId: 'test',
        secretAccessKey: 'testtesttest'
      }
    } : endpoint ? { endpoint } : {})
  });
  if (!localUseAWS && (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test')) {
    // Setup the necessary buckets lazily
    createLocalBuckets(s3).catch(() => {});
  }
    
  return s3;
}

/**
 * For development and testing purposes only, sets up the necessary buckets for dev and CI/CD
 */
export async function getS3Local(): Promise<S3> {
  const s3 = getS3();
  await createLocalBuckets(s3);
  return s3;
}

const createLocalBuckets = async (s3: S3) => {
  if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
    await Promise.all([
      s3.createBucket({ Bucket: 'aidkit-documents',
        ObjectLockEnabledForBucket: true,
      }).catch(() => {}),
      s3.createBucket({ Bucket: 'aidkit-signatures', ObjectLockEnabledForBucket: true}).catch(() => {}),
      s3.createBucket({ Bucket: surveysBucketName, ObjectLockEnabledForBucket: true }).catch(() => {}),
      s3.putBucketVersioning({ Bucket: surveysBucketName, VersioningConfiguration: { Status: 'Enabled' } }).catch(() => {}),
      s3.createBucket({ Bucket: 'aidkit-oit-production', ObjectLockEnabledForBucket: false }).catch(() => {}),
      s3.createBucket({ Bucket: 'aidkit-public', ObjectLockEnabledForBucket: false }).catch(() => {}),
      s3.createBucket({ Bucket: 'aidkit-exports', ObjectLockEnabledForBucket: false }).catch(() => {}),
    ]);
    // MinIO ignores S3-style ACLs, so set explicit public-read bucket policies
    const publicReadPolicy = (bucket: string) => JSON.stringify({
      Version: '2012-10-17',
      Statement: [{
        Effect: 'Allow',
        Principal: { AWS: ['*'] },
        Action: ['s3:GetObject'],
        Resource: [`arn:aws:s3:::${bucket}/*`]
      }]
    });
    await Promise.all([
      s3.putBucketPolicy({ Bucket: 'aidkit-signatures', Policy: publicReadPolicy('aidkit-signatures') }).catch(() => {}),
      s3.putBucketPolicy({ Bucket: 'aidkit-public', Policy: publicReadPolicy('aidkit-public') }).catch(() => {}),
    ]);
  } else {
    throw Error('This function should only be called in development or test environments');
  }
}
